/**
 * SignaturPage - Administration › E-Mail-Signatur (B3-2, rich upgrade).
 *
 * WHAT CHANGED. The signature body used to be a plain-text box. Brokers asked for a
 * signature "as rich as Outlook's": bold/italic, hyperlinks and an inline logo. So the plain
 * body box is replaced by a small rich editor (`RichSignatureEditor` - a contentEditable +
 * toolbar), and the body is now stored as SANITISED HTML.
 *
 * THE STRUCTURED HEADER STAYS. Name / Funktion / Telefon remain separate fields feeding the
 * org-wide template on the SERVER (the firm's name is added there, which the browser cannot
 * know). The rich editor is the body/footer beneath that header. This keeps the "consistent
 * across the firm" promise for the header while letting the body carry a logo and formatting.
 *
 * THE PREVIEW IS THE SERVER'S `renderedHtml`, never a client-side rebuild of the full
 * signature - the same reason as before: only the server knows the firm name and only the
 * server's sanitiser is authoritative. The editor shows its own live "bereinigt" preview of
 * the body while typing; this panel shows the whole thing as the server last rendered it,
 * refreshed after each save.
 *
 * [Source: apps/web/src/features/administration/pages/RichSignatureEditor.tsx]
 * [Source: apps/api/src/modules/communication/email-signature.service.ts] (renderSignatureHtml)
 */

import { useEffect, useState } from 'react';
import { AlertCircle, Check, Loader2 } from 'lucide-react';
import { apiClient } from '../../../lib/api-client';
import type { EmailSignatureWire } from '../../../lib/api-client';
import { sanitizeSignatureHtml } from '../../../lib/sanitize-signature-html';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { Button, TextInput } from '../components';
import { RichSignatureEditor } from './RichSignatureEditor';

const meta = ADMIN_PAGE_META.signatur;

/** Escape a legacy plain-text body into HTML so it shows correctly in the rich editor. */
function plainToHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r?\n/g, '<br>');
}

export function SignaturPage() {
  const [displayName, setDisplayName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');
  /** The rich body HTML as the editor currently holds it. */
  const [bodyHtml, setBodyHtml] = useState('');
  /** The HTML the editor is seeded with (bodyHtml, or an escaped legacy body). */
  const [initialBodyHtml, setInitialBodyHtml] = useState('');
  /** Re-mounts the (uncontrolled) editor when a freshly-loaded signature arrives. */
  const [editorKey, setEditorKey] = useState(0);

  const [renderedHtml, setRenderedHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function apply(sig: EmailSignatureWire | null) {
    setDisplayName(sig?.displayName ?? '');
    setJobTitle(sig?.jobTitle ?? '');
    setPhone(sig?.phone ?? '');
    const seed = sig?.bodyHtml ?? (sig?.bodyText ? plainToHtml(sig.bodyText) : '');
    setBodyHtml(seed);
    setInitialBodyHtml(seed);
    setRenderedHtml(sig?.renderedHtml ?? null);
    setEditorKey((k) => k + 1);
  }

  useEffect(() => {
    let cancelled = false;
    apiClient.emailSignature
      .get()
      .then((res) => {
        if (!cancelled) {
          apply(res.signature);
          setError(null);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Die Signatur konnte nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (displayName.trim().length === 0) {
      setError('Der Name darf nicht leer sein.');
      return;
    }
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      // The PUT returns the server rendering, so the preview below is what the server
      // produced (and sanitised) rather than anything this page assembled. bodyText is
      // cleared: the rich HTML body supersedes the legacy plain one.
      const updated = await apiClient.emailSignature.put({
        displayName: displayName.trim(),
        jobTitle: jobTitle.trim() || null,
        phone: phone.trim() || null,
        bodyText: null,
        bodyHtml: bodyHtml.trim() || null,
      });
      apply(updated);
      setSaved(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Die Signatur konnte nicht gespeichert werden.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <AdminPageHeader title={meta.title} description={meta.description} />

      {loading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Wird geladen …
        </p>
      )}

      {!loading && (
        <div className="grid gap-6 lg:grid-cols-2">
          <form onSubmit={handleSave} className="flex flex-col gap-4 rounded-lg border bg-card p-4">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs font-medium text-muted-foreground">Name</span>
              <TextInput
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Hans Muster"
                aria-label="Name"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs font-medium text-muted-foreground">Funktion</span>
              <TextInput
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="Kundenberater"
                aria-label="Funktion"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs font-medium text-muted-foreground">Telefon</span>
              <TextInput
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+41 44 111 22 33"
                aria-label="Telefon"
              />
            </label>

            <div className="flex flex-col gap-1 text-sm">
              <span className="text-xs font-medium text-muted-foreground">
                Signatur-Inhalt (Fett, Kursiv, Links, Logo)
              </span>
              <RichSignatureEditor
                key={editorKey}
                initialHtml={initialBodyHtml}
                onChange={setBodyHtml}
                ariaLabel="Signatur-Inhalt"
              />
            </div>

            {error !== null && (
              <p className="flex items-start gap-2 text-sm text-destructive" role="alert">
                <AlertCircle className="size-4 shrink-0" />
                {error}
              </p>
            )}

            <div className="flex items-center gap-3">
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="size-4 animate-spin" /> : null}
                Speichern
              </Button>
              {saved && (
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Check className="size-4" />
                  Gespeichert
                </span>
              )}
            </div>
          </form>

          <section className="rounded-lg border bg-card p-4" aria-label="Vorschau">
            <h3 className="mb-2 text-sm font-semibold">Vorschau</h3>
            <p className="mb-3 text-xs text-muted-foreground">
              So wird Ihre Signatur in ausgehende E-Mails eingefügt. Der Server bereinigt die
              Signatur beim Speichern (nur erlaubte Formatierung bleibt erhalten).
            </p>
            {renderedHtml !== null && renderedHtml.trim().length > 0 ? (
              <div
                className="rounded-md bg-muted p-3 text-sm text-foreground [&_a]:text-primary [&_a]:underline [&_img]:max-w-[240px]"
                // The server's renderedHtml is already sanitised; the client sanitiser is a
                // second net before it ever reaches the DOM.
                dangerouslySetInnerHTML={{ __html: sanitizeSignatureHtml(renderedHtml) }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                Noch keine Signatur hinterlegt. Speichern Sie das Formular, um eine anzulegen.
              </p>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
